import { supabase } from '@/integrations/supabase/client';

interface IconValidationResult {
  isValid: boolean;
  url?: string;
  error?: string;
  fallback?: string;
}

interface IconMetrics {
  totalIcons: number;
  validIcons: number;
  brokenIcons: number;
  totalSize: number;
  averageLoadTime: number;
}

interface FallbackRule {
  pattern: RegExp;
  fallback: string;
  description: string;
}

/**
 * Central icon management utility for Supabase storage icons
 */
export class IconManager {
  private static instance: IconManager;
  private cache = new Map<string, IconValidationResult>();
  private metrics: IconMetrics = {
    totalIcons: 0,
    validIcons: 0,
    brokenIcons: 0,
    totalSize: 0,
    averageLoadTime: 0
  };

  // Fallback rules for different icon categories
  private fallbackRules: FallbackRule[] = [
    {
      pattern: /avatar|character|lyra/i,
      fallback: 'lyra-avatar.png',
      description: 'Character and avatar icons'
    },
    {
      pattern: /badge|achievement|trophy|award/i,
      fallback: 'achievement-trophy.png',
      description: 'Achievement and badge icons'
    },
    {
      pattern: /user|role|person/i,
      fallback: 'user-role-it.png',
      description: 'User and role icons'
    },
    {
      pattern: /chapter|progress|learning/i,
      fallback: 'chapter-progress.png',
      description: 'Learning and progress icons'
    },
    {
      pattern: /communication|message|chat/i,
      fallback: 'communication.png',
      description: 'Communication icons'
    },
    {
      pattern: /data|analytics|chart/i,
      fallback: 'data-analytics.png',
      description: 'Data and analytics icons'
    },
    {
      pattern: /hero|main|primary/i,
      fallback: 'hero-main.png',
      description: 'Primary and hero icons'
    },
    {
      pattern: /nav|logo|brand/i,
      fallback: 'navbar-logo.png',
      description: 'Navigation and branding icons'
    },
    {
      pattern: /error|problem|issue/i,
      fallback: 'error-state-friendly.png',
      description: 'Error state icons'
    },
    {
      pattern: /empty|blank|missing/i,
      fallback: 'empty-state-welcome.png',
      description: 'Empty state icons'
    },
    {
      pattern: /loading|spinner|wait/i,
      fallback: 'loading-lyra.png',
      description: 'Loading state icons'
    }
  ];

  private constructor() {}

  static getInstance(): IconManager {
    if (!IconManager.instance) {
      IconManager.instance = new IconManager();
    }
    return IconManager.instance;
  }

  /**
   * Get public URL for an icon with validation
   */
  async getIconUrl(iconPath: string, enableFallback: boolean = true): Promise<string> {
    try {
      // Check cache first
      const cached = this.cache.get(iconPath);
      if (cached) {
        return cached.isValid && cached.url ? cached.url : this.getDirectUrl(iconPath);
      }

      // Validate icon and cache result
      const validation = await this.validateIcon(iconPath);
      this.cache.set(iconPath, validation);

      if (validation.isValid && validation.url) {
        return validation.url;
      }

      // Try fallback if enabled
      if (enableFallback && validation.fallback) {
        console.warn(`Using fallback for ${iconPath}: ${validation.fallback}`);
        return this.getDirectUrl(validation.fallback);
      }

      // Return direct URL as last resort
      return this.getDirectUrl(iconPath);

    } catch (error) {
      console.error(`Failed to get icon URL for ${iconPath}:`, error);
      return this.getDirectUrl(enableFallback ? this.getDefaultFallback() : iconPath);
    }
  }

  /**
   * Validate if an icon exists and is accessible
   */
  async validateIcon(iconPath: string): Promise<IconValidationResult> {
    const startTime = Date.now();
    
    try {
      const url = this.getDirectUrl(iconPath);
      
      // Test if the image loads
      const isValid = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });

      const loadTime = Date.now() - startTime;
      this.updateMetrics(isValid, loadTime);

      if (isValid) {
        return { isValid: true, url };
      } else {
        const fallback = this.findFallback(iconPath);
        return {
          isValid: false,
          error: 'Failed to load image',
          fallback
        };
      }

    } catch (error) {
      const fallback = this.findFallback(iconPath);
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        fallback
      };
    }
  }

  /**
   * Batch validate multiple icons
   */
  async validateIcons(iconPaths: string[]): Promise<Map<string, IconValidationResult>> {
    const results = new Map<string, IconValidationResult>();
    
    const promises = iconPaths.map(async (iconPath) => {
      const result = await this.validateIcon(iconPath);
      results.set(iconPath, result);
    });

    await Promise.allSettled(promises);
    return results;
  }

  /**
   * Find appropriate fallback for an icon
   */
  private findFallback(iconPath: string): string {
    const fileName = iconPath.toLowerCase();
    
    for (const rule of this.fallbackRules) {
      if (rule.pattern.test(fileName)) {
        return rule.fallback;
      }
    }
    
    return this.getDefaultFallback();
  }

  /**
   * Get direct Supabase URL without validation
   */
  private getDirectUrl(iconPath: string): string {
    const { data } = supabase.storage
      .from('app-icons')
      .getPublicUrl(iconPath);
    return data.publicUrl;
  }

  /**
   * Get default fallback icon
   */
  private getDefaultFallback(): string {
    return 'lyra-avatar.png';
  }

  /**
   * Update performance metrics
   */
  private updateMetrics(isValid: boolean, loadTime: number): void {
    this.metrics.totalIcons++;
    if (isValid) {
      this.metrics.validIcons++;
    } else {
      this.metrics.brokenIcons++;
    }
    
    // Update average load time
    const totalLoadTime = this.metrics.averageLoadTime * (this.metrics.totalIcons - 1) + loadTime;
    this.metrics.averageLoadTime = totalLoadTime / this.metrics.totalIcons;
  }

  /**
   * Get performance metrics
   */
  getMetrics(): IconMetrics {
    return { ...this.metrics };
  }

  /**
   * Clear cache and reset metrics
   */
  reset(): void {
    this.cache.clear();
    this.metrics = {
      totalIcons: 0,
      validIcons: 0,
      brokenIcons: 0,
      totalSize: 0,
      averageLoadTime: 0
    };
  }

  /**
   * Preload critical icons
   */
  async preloadCriticalIcons(): Promise<void> {
    const criticalIcons = [
      'lyra-avatar.png',
      'navbar-logo.png',
      'hero-main.png',
      'loading-lyra.png',
      'achievement-trophy.png',
      'learning-target.png',
      'mission-heart.png',
      'communication.png',
      'data-analytics.png',
      'error-state-friendly.png',
      'empty-state-welcome.png'
    ];

    console.log('🚀 Preloading critical icons...');
    const startTime = Date.now();
    
    await this.validateIcons(criticalIcons);
    
    const duration = Date.now() - startTime;
    const validCount = criticalIcons.filter(icon => {
      const result = this.cache.get(icon);
      return result?.isValid;
    }).length;
    
    console.log(`✅ Preloaded ${validCount}/${criticalIcons.length} critical icons in ${duration}ms`);
  }

  /**
   * Generate icon usage report
   */
  generateReport(): {
    metrics: IconMetrics;
    fallbackRules: FallbackRule[];
    cacheStatus: { size: number; entries: Array<{ path: string; valid: boolean; fallback?: string }> };
    recommendations: string[];
  } {
    const cacheEntries = Array.from(this.cache.entries()).map(([path, result]) => ({
      path,
      valid: result.isValid,
      fallback: result.fallback
    }));

    const recommendations: string[] = [];
    
    if (this.metrics.brokenIcons > 0) {
      recommendations.push(`${this.metrics.brokenIcons} broken icons need attention`);
    }
    
    if (this.metrics.averageLoadTime > 500) {
      recommendations.push('Consider optimizing icon sizes for better performance');
    }
    
    const validationRate = (this.metrics.validIcons / this.metrics.totalIcons) * 100;
    if (validationRate < 90) {
      recommendations.push('Icon validation rate is below 90% - review icon paths');
    }

    return {
      metrics: this.metrics,
      fallbackRules: this.fallbackRules,
      cacheStatus: {
        size: this.cache.size,
        entries: cacheEntries
      },
      recommendations
    };
  }
}

// Export singleton instance
export const iconManager = IconManager.getInstance();
export default iconManager;